import { deepmerge } from 'deepmerge-ts'
import { default as dot } from 'dot-object'
import { joi } from '@nhtio/lucid-resourceful/joi'
import { ResourcefulRouterOperation, ResourcefulRouterVerb } from '../types'
import { E_INVALID_RESOURCEFUL_ROUTER_OPTIONS } from '@nhtio/lucid-resourceful/errors'
import { ResourcefulErrorMiddleware } from '../middlewares/resourceful_error_middleware'
import {
  SchemaOrReferenceObjectSchema as schemaOrReferenceObjectSchema,
  InfoObjectSchema as infoObjectSchema,
  ExternalDocumentationObjectSchema as externalDocsSchema,
  SecuritySchemeObjectSchema,
} from '../utils/openapi_validation_schemas'
import type { ValidationError } from '@nhtio/lucid-resourceful/joi'
import type { ResourcefulErrorMiddlewareOptions } from '../middlewares/resourceful_error_middleware'
import type {
  ResourcefulRouterOptions,
  ResourcefulSecuritySchemaDefinition,
  RouteCallback,
  AdditionalResourcefulRoutesMap,
  AdditionalResourcefulRouteDetails,
  ResourcefulPolicy,
  ResourcefulIndexResponseHook,
  ResourceResourcefulRecordResponseHook,
  ResourcefulRouterMutatorOptions,
} from '../types'

const DEFAULT_RESOURCEFUL_ROUTER_OPTIONS: ResourcefulRouterOptions = {
  prefix: '/',
  domain: undefined,
  middleware: [],
  except: [],
  additional: {},
  info: {
    title: 'Resourceful API',
    version: '0.0.0',
  },
  tagMap: {
    general: 'General',
    additional: 'Additional',
  },
  catchThrown: true,
  headers: {},
  security: [],
  scopeRestrictors: [],
  payloadRestrictors: [],
  policies: [],
  mutators: {},
}

const FORBIDDEN_ADDITIONALS = ['get|/']

const FORBIDDEN_ADDITIONAL_PATTERNS = [
  /^get\|\/?[\w.-]+\/?$/,
  /^get\|\/?[\w.-]+\/\$meta\.index$/,
  /^post\|\/?[\w.-]+\/?$/,
  /^get\|\/?[\w.-]+\/\$meta\.create$/,
  /^get\|\/?[\w.-]+\/:id$/,
  /^get\|\/?[\w.-]+\/:id\/\$meta\.read$/,
  /^get\|\/?[\w.-]+\/:id\/:relationship$/,
  /^get\|\/?[\w.-]+\/:id\/:relationship\/\$meta\.index$/,
  /^put\|\/?[\w.-]+\/:id$/,
  /^patch\|\/?[\w.-]+\/:id$/,
  /^get\|\/?[\w.-]+\/\$meta\.update$/,
  /^put\|\/?[\w.-]+\/\$bulk$/,
  /^put\|\/?[\w.-]+\/\$bulk\/:ids$/,
  /^patch\|\/?[\w.-]+\/\$bulk$/,
  /^patch\|\/?[\w.-]+\/\$bulk\/:ids$/,
  /^get\|\/?[\w.-]+\/\$meta\.\$bulk\.update$/,
  /^put\|\/?[\w.-]+\/:id\/:relationship$/,
  /^patch\|\/?[\w.-]+\/:id\/:relationship$/,
  /^get\|\/?[\w.-]+\/:id\/:relationship\/\$meta\.sync$/,
  /^delete\|\/?[\w.-]+\/:id$/,
  /^get\|\/?[\w.-]+\/\$meta\.delete$/,
]

let ForbiddenAdditionalPatternsSchema = joi.string()
FORBIDDEN_ADDITIONAL_PATTERNS.forEach((p) => {
  ForbiddenAdditionalPatternsSchema = ForbiddenAdditionalPatternsSchema.concat(
    joi.string().regex(p, { invert: true })
  )
})

const RouteCallbackSchema = joi.function<RouteCallback>().required()

const AdditionalResourcefulRouteDetailsSchema = joi.object<AdditionalResourcefulRouteDetails>({
  title: joi.string().optional(),
  description: joi.string().optional(),
  handler: RouteCallbackSchema,
  requestPayloadSchema: schemaOrReferenceObjectSchema.optional(),
  responsePayloadSchema: schemaOrReferenceObjectSchema.optional(),
  tags: joi.alternatives(joi.string(), joi.array().items(joi.string())).optional(),
})

const AdditionalResourcefulRoutesMapValueSchema = joi.alternatives<
  RouteCallback | AdditionalResourcefulRouteDetails
>(RouteCallbackSchema, AdditionalResourcefulRouteDetailsSchema)

const ResourcefulRouterOptionsMiddlewareSchema = joi.alternatives(
  joi.function(),
  joi
    .object({
      name: joi.string().required(),
      args: joi.array().optional(),
    })
    .unknown(true)
)

const ResourcefulSecuritySchemaDefinitionSchema = joi.object<ResourcefulSecuritySchemaDefinition>({
  key: joi.string().required(),
  schema: SecuritySchemeObjectSchema.required(),
  applyToModels: joi.alternatives(
    joi.boolean(),
    joi
      .object()
      .pattern(
        joi.string(),
        joi.alternatives(
          joi.boolean(),
          joi
            .object()
            .pattern(
              joi.string().valid(...ResourcefulRouterVerb),
              joi.alternatives(joi.boolean(), joi.array().items(joi.string()))
            )
        )
      )
      .required()
  ),
  applyToAdditional: joi
    .alternatives(
      joi.boolean(),
      joi
        .object()
        .pattern(
          joi
            .string()
            .regex(
              /^(get|post|put|patch|delete)\|\/(?:[\w\-\.]+(?:\/[\w\-\.]+)*(?:\/:\w+(?:\([^)]+\))?(?:\?)?)*(?:\/\*)?)?$/
            ),
          joi.alternatives(joi.boolean(), joi.array().items(joi.string()))
        )
    )
    .required(),
})

const ResourcefulIndexResponseHookSchema = joi
  .array<ResourcefulIndexResponseHook[]>()
  .items(
    joi.alternatives(joi.function().arity(0), joi.function().arity(1), joi.function().arity(2))
  )
const ResourceResourcefulRecordResponseHookSchema = joi
  .array<ResourceResourcefulRecordResponseHook[]>()
  .items(
    joi.alternatives(joi.function().arity(0), joi.function().arity(1), joi.function().arity(2))
  )

export const ResourcefulRouterMutatorOptionsSchema = joi.object<ResourcefulRouterMutatorOptions>({
  index: ResourcefulIndexResponseHookSchema.optional(),
  create: ResourceResourcefulRecordResponseHookSchema.optional(),
  read: ResourceResourcefulRecordResponseHookSchema.optional(),
  readRelated: ResourceResourcefulRecordResponseHookSchema.optional(),
  update: ResourceResourcefulRecordResponseHookSchema.optional(),
})

export const ResourcefulPolicySchema = joi
  .object<ResourcefulPolicy>({
    scope: joi
      .alternatives(
        joi.function().arity(0),
        joi.function().arity(1),
        joi.function().arity(2),
        joi.function().arity(3)
      )
      .optional(),
    payload: joi
      .alternatives(joi.function().arity(0), joi.function().arity(1), joi.function().arity(2))
      .optional(),
    mutators: ResourcefulRouterMutatorOptionsSchema.optional(),
  })
  .unknown(false)
  .or('scope', 'payload', 'mutators')

const ResourcefulRouterOptionsSchema = joi.object<ResourcefulRouterOptions>({
  prefix: joi.string().pattern(/^\//).required(),
  domain: joi.string().optional().allow(null),
  middleware: joi
    .alternatives(
      ResourcefulRouterOptionsMiddlewareSchema,
      joi.array().items(ResourcefulRouterOptionsMiddlewareSchema)
    )
    .required(),
  except: joi
    .array()
    .items(joi.string().valid(...ResourcefulRouterOperation))
    .required(),
  additional: joi
    .object<AdditionalResourcefulRoutesMap>()
    .pattern(
      joi
        .string()
        .regex(
          /^(get|post|put|patch|delete)\|\/(?:[\w\-\.]+(?:\/[\w\-\.]+)*(?:\/:\w+(?:\([^)]+\))?(?:\?)?)*(?:\/\*)?)?$/
        )
        .disallow(...FORBIDDEN_ADDITIONALS)
        .concat(ForbiddenAdditionalPatternsSchema),

      AdditionalResourcefulRoutesMapValueSchema
    )
    .required(),
  info: infoObjectSchema.required(),
  externalDocs: externalDocsSchema.optional(),
  tagMap: joi
    .object({
      general: joi.string().required(),
    })
    .unknown(true)
    .required(),
  catchThrown: joi.boolean().default(true),
  onVineValidationError: joi.function().arity(1).optional(),
  onJoiValidationError: joi.function().arity(1).optional(),
  onException: joi.function().arity(1).optional(),
  onError: joi.function().arity(1).optional(),
  onUnknown: joi.function().arity(1).optional(),
  onAny: joi.function().arity(1).optional(),
  headers: joi.object().pattern(joi.string(), joi.string()).default({}),
  security: joi.array().items(ResourcefulSecuritySchemaDefinitionSchema),
  scopeRestrictors: joi
    .array()
    .items(
      joi.alternatives(
        joi.function().arity(0),
        joi.function().arity(1),
        joi.function().arity(2),
        joi.function().arity(3)
      )
    )
    .optional()
    .default([]),
  payloadRestrictors: joi
    .array()
    .items(
      joi.alternatives(joi.function().arity(0), joi.function().arity(1), joi.function().arity(2))
    )
    .optional()
    .default([]),
  policies: joi.array().items(ResourcefulPolicySchema).optional().default([]),
  mutators: ResourcefulRouterMutatorOptionsSchema.optional().default({}),
})

export class ResourcefulRouterOptionsService {
  readonly #options: ResourcefulRouterOptions

  private constructor(options: ResourcefulRouterOptions) {
    const modified = { ...options }
    if (options.catchThrown === true) {
      const errorMiddlewareOptions: ResourcefulErrorMiddlewareOptions = {
        onVineValidationError: options.onVineValidationError,
        onJoiValidationError: options.onJoiValidationError,
        onException: options.onException,
        onError: options.onError,
        onUnknown: options.onUnknown,
        onAny: options.onAny,
        asYaml: true,
        headers: options.headers,
      }
      if (!Array.isArray(modified.middleware)) {
        modified.middleware = [modified.middleware].filter(Boolean)
      }
      modified.middleware.unshift(ResourcefulErrorMiddleware.usable(errorMiddlewareOptions))
    }
    this.#options = Object.freeze(deepmerge({}, modified))
    Object.defineProperty(this, 'prefix', {
      get: () => this.#options.prefix,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'domain', {
      get: () => this.#options.domain,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'middleware', {
      get: () => this.#options.middleware,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'except', {
      get: () => this.#options.except,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'additional', {
      get: () => this.#options.additional,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'info', {
      get: () => this.#options.info,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'externalDocs', {
      get: () => this.#options.externalDocs,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'tagMap', {
      get: () => this.#options.tagMap,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'catchThrown', {
      get: () => this.#options.catchThrown,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'onVineValidationError', {
      get: () => this.#options.onVineValidationError,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'onJoiValidationError', {
      get: () => this.#options.onJoiValidationError,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'onException', {
      get: () => this.#options.onException,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'onError', {
      get: () => this.#options.onError,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'onUnknown', {
      get: () => this.#options.onUnknown,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'onAny', {
      get: () => this.#options.onAny,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'headers', {
      get: () => this.#options.headers,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'security', {
      get: () => this.#options.security,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'scopeRestrictors', {
      get: () => this.#options.scopeRestrictors,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'payloadRestrictors', {
      get: () => this.#options.payloadRestrictors,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'policies', {
      get: () => this.#options.policies,
      enumerable: true,
      configurable: false,
    })
    Object.defineProperty(this, 'mutators', {
      get: () => this.#options.mutators,
      enumerable: true,
      configurable: false,
    })
  }

  declare prefix: Readonly<ResourcefulRouterOptions['prefix']>
  declare domain: Readonly<ResourcefulRouterOptions['domain']>
  declare middleware: Readonly<ResourcefulRouterOptions['middleware']>
  declare except: Readonly<ResourcefulRouterOptions['except']>
  declare additional: Readonly<ResourcefulRouterOptions['additional']>
  declare info: Readonly<ResourcefulRouterOptions['info']>
  declare externalDocs: Readonly<ResourcefulRouterOptions['externalDocs']>
  declare tagMap: Readonly<ResourcefulRouterOptions['tagMap']>
  declare catchThrown: Readonly<ResourcefulRouterOptions['catchThrown']>
  declare onVineValidationError: ResourcefulRouterOptions['onVineValidationError']
  declare onJoiValidationError: ResourcefulRouterOptions['onJoiValidationError']
  declare onException: ResourcefulRouterOptions['onException']
  declare onError: ResourcefulRouterOptions['onError']
  declare onUnknown: ResourcefulRouterOptions['onUnknown']
  declare onAny: ResourcefulRouterOptions['onAny']
  declare headers: Readonly<ResourcefulRouterOptions['headers']>
  declare security: Readonly<ResourcefulRouterOptions['security']>
  declare scopeRestrictors: ResourcefulRouterOptions['scopeRestrictors']
  declare payloadRestrictors: ResourcefulRouterOptions['payloadRestrictors']
  declare policies: ResourcefulRouterOptions['policies']
  declare mutators: ResourcefulRouterOptions['mutators']

  get(key: string): any {
    return dot.pick(key, this.#options, false)
  }

  public static init(options: unknown): ResourcefulRouterOptionsService {
    // step 1: at minimum, ensure that we are dealing with an object
    const { error: checkIfObjectError } = joi.object().unknown(true).required().validate(options, {
      abortEarly: true,
    })
    if (checkIfObjectError) {
      throw new E_INVALID_RESOURCEFUL_ROUTER_OPTIONS(checkIfObjectError)
    }
    // step 2: create an object which is merged with the defaults
    const mergedOptions = deepmerge(
      DEFAULT_RESOURCEFUL_ROUTER_OPTIONS,
      options
    ) as ResourcefulRouterOptions
    // step 3: validate the merged options
    const { error: resourcefulRouterOptionsValidationError } =
      ResourcefulRouterOptionsSchema.validate(mergedOptions, {
        abortEarly: true,
        convert: true,
        allowUnknown: true,
        stripUnknown: true,
      }) as { error?: ValidationError; value: ResourcefulRouterOptions }
    if (resourcefulRouterOptionsValidationError) {
      throw new E_INVALID_RESOURCEFUL_ROUTER_OPTIONS(resourcefulRouterOptionsValidationError)
    }
    const instance = new ResourcefulRouterOptionsService(mergedOptions)
    return instance
  }
}
